import React, { useEffect, useState } from 'react';
import { RefreshCw, Save, ArrowRight, ArrowLeft, UserCog, Calendar, Check, X, Palmtree } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function PlanningGeneratorResponsables() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [responsables, setResponsables] = useState([]);
  const [generatedPlanning, setGeneratedPlanning] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  
  // Planning manuel par responsable et par jour
  const [planningData, setPlanningData] = useState({});

  useEffect(() => {
    fetchResponsables();
    
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? 0 : -dayOfWeek;
    const sunday = new Date(now);
    sunday.setDate(now.getDate() + diff);
    sunday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(sunday);
  }, []);

  const fetchResponsables = async () => {
    try {
      const response = await api.get('/employees/responsables');
      setResponsables(response.data);
      
      // Initialiser le planning vide pour chaque responsable
      const initialPlanning = {};
      response.data.forEach(resp => {
        initialPlanning[resp.id] = {
          dim: '',
          lun: '',
          mar: '',
          mer: '',
          jeu: '',
          ven: '',
          sam: ''
        };
      });
      setPlanningData(initialPlanning);
    } catch (error) {
      console.error('Error fetching responsables:', error);
      toast.error('Erreur lors du chargement des responsables');
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const dayKeys = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const typeOptions = [
    { value: '', label: '-' },
    { value: 'travail', label: 'Travail' },
    { value: 'reunion', label: 'Réunion' },
    { value: 'formation', label: 'Formation' },
    { value: 'supervision', label: 'Supervision' },
    { value: 'conge_paye', label: 'CP' },
    { value: 'absent', label: 'Absent' },
    { value: 'arret_maladie', label: 'Maladie' }
  ];

  const updatePlanning = (responsableId, dayKey, value) => {
    setPlanningData(prev => ({
      ...prev,
      [responsableId]: {
        ...prev[responsableId],
        [dayKey]: value
      }
    }));
  };

  const generatePlanning = () => {
    const planning = [];
    
    responsables.forEach(resp => {
      const respPlanning = planningData[resp.id];
      if (!respPlanning) return;
      
      weekDays.forEach((day, idx) => {
        const dayKey = dayKeys[idx];
        const type = respPlanning[dayKey];
        
        if (type && type !== '') {
          planning.push({
            employee_id: resp.id,
            employee_nom: `${resp.prenom} ${resp.nom}`,
            date: day.toISOString(),
            type: type,
            heure_debut: type === 'conge_paye' || type === 'absent' ? '' : '09:00',
            heure_fin: type === 'conge_paye' || type === 'absent' ? '' : '18:00'
          });
        }
      });
    });
    
    setGeneratedPlanning(planning);
    toast.success(`Planning responsables généré (${planning.length} entrées)`);
  };

  const savePlanning = async () => {
    if (generatedPlanning.length === 0) {
      toast.error('Veuillez d\'abord générer le planning');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/planning/save', generatedPlanning);
      toast.success('Planning responsables enregistré !');
      setTimeout(() => {
        navigate('/planning');
      }, 1000);
    } catch (error) {
      console.error('Error saving planning:', error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'travail': return 'bg-green-100 text-green-700';
      case 'reunion': return 'bg-indigo-100 text-indigo-700';
      case 'formation': return 'bg-teal-100 text-teal-700';
      case 'supervision': return 'bg-amber-100 text-amber-700';
      case 'conge_paye': return 'bg-blue-100 text-blue-700';
      case 'absent': return 'bg-red-100 text-red-700';
      case 'arret_maladie': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const setAllDays = (responsableId, value) => {
    setPlanningData(prev => ({
      ...prev,
      [responsableId]: {
        dim: value,
        lun: value,
        mar: value,
        mer: value,
        jeu: value,
        ven: value,
        sam: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!hasRole(['admin', 'responsable'])) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Accès non autorisé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-generator-responsables">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/planning/generator')}>
            <ArrowLeft size={18} className="mr-2" />
            Retour Chauffeurs
          </Button>
          <div>
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <UserCog size={24} />
              Planning Responsables
            </h2>
            <p className="text-slate-600">
              Semaine du {formatDate(weekDays[0])} au {formatDate(weekDays[6])}
            </p>
          </div>
        </div>
        
        <div className="bg-brand/10 border border-brand/30 rounded-lg p-4 text-center">
          <div className="text-sm font-medium text-brand mb-1">Responsables</div>
          <div className="text-3xl font-bold text-brand">{responsables.length}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={generatePlanning}
          className="bg-brand hover:bg-brand-dark"
          data-testid="generate-responsables-button"
        >
          <RefreshCw size={18} className="mr-2" />
          Générer le planning
        </Button>
        {generatedPlanning.length > 0 && (
          <>
            <Button onClick={savePlanning} variant="outline" disabled={saving}>
              <Save size={18} className="mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button onClick={() => navigate('/planning')} variant="outline">
              <ArrowRight size={18} className="mr-2" />
              Voir le planning
            </Button>
          </>
        )}
      </div>

      {/* Légende des types */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-3">Types d'activités</h3>
        <div className="flex flex-wrap gap-2">
          {typeOptions.filter(t => t.value !== '').map(type => (
            <div key={type.value} className={`px-3 py-1 rounded-lg text-xs font-medium ${getTypeColor(type.value)}`}>
              {type.label}
            </div>
          ))}
        </div>
      </div>

      {/* Tableau de planification */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <h3 className="font-semibold">Planification manuelle</h3>
          <p className="text-sm text-slate-600">Sélectionnez le type d'activité pour chaque jour</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-3 border-b border-slate-200 min-w-[200px]">Responsable</th>
                <th className="text-center p-3 border-b border-slate-200 w-24">Tout</th>
                {dayNames.map((name, idx) => (
                  <th key={idx} className="text-center p-3 border-b border-slate-200 w-28">
                    {name}
                    <div className="text-xs font-normal text-slate-500">
                      {weekDays[idx]?.getDate()}/{weekDays[idx]?.getMonth() + 1}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {responsables.length > 0 ? (
                responsables.map((resp, index) => {
                  const respPlanning = planningData[resp.id] || {};
                  
                  return (
                    <tr key={resp.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand text-white rounded-full flex items-center justify-center font-medium">
                            {resp.prenom?.[0]}{resp.nom?.[0]}
                          </div>
                          <div>
                            <p className="font-medium">{resp.prenom} {resp.nom}</p>
                            <p className="text-xs text-slate-500">{resp.poste}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 border-b border-slate-100 text-center">
                        <select
                          onChange={(e) => setAllDays(resp.id, e.target.value)}
                          className="text-xs border border-slate-300 rounded px-2 py-1"
                        >
                          {typeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      {dayKeys.map((dayKey, idx) => {
                        const value = respPlanning[dayKey] || '';
                        return (
                          <td key={idx} className="p-3 border-b border-slate-100 text-center">
                            <select
                              value={value}
                              onChange={(e) => updatePlanning(resp.id, dayKey, e.target.value)}
                              className={`w-full text-xs font-medium px-2 py-2 rounded border cursor-pointer ${value ? getTypeColor(value) : 'bg-white border-slate-300'}`}
                            >
                              {typeOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-500">
                    <UserCog size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Aucun responsable trouvé</p>
                    <p className="text-sm">Ajoutez des responsables dans la section Salariés</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aperçu du planning généré */}
      {generatedPlanning.length > 0 && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <h3 className="text-sm font-semibold mb-3 text-green-700">
            Aperçu ({generatedPlanning.length} entrées)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {generatedPlanning.map((entry, idx) => (
              <div key={idx} className={`px-3 py-2 rounded-lg text-xs ${getTypeColor(entry.type)}`}>
                <span className="font-medium">{entry.employee_nom}</span>
                <span className="mx-2">•</span>
                <span>{new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}</span>
                <span className="mx-2">•</span>
                <span className="capitalize">{entry.type.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
